import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedSocialLinksWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../../../common/types';
import { WidgetEditionProps } from '../../props';
import { SMLFormContainer } from '../../../../SMLFormContainer/SMLFormContainer';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { SMLButton } from '../../../../../common/views/SMLButton/SMLButton';

/**
 * The social links block has no list of its own. It shows the artist's
 * profiles from Catalog → Service connections and Promote → Channels, so the
 * editor only says where those live.
 *
 * design: artist-settings c (Social links block note)
 */
const SocialWidgetEditionViewComponent: React.FC<WidgetEditionProps<SerializedSocialLinksWidget>> = ({
  widget,
  saveWidget,
  deleteWidget,
  isLoading,
  error,
  accountId,
  smartLinkId,
}) => {
  const intl = useIntl();
  const onSave = useCallback(() => {
    saveWidget({}, SmartLinkWidgetType.socialLinks).catch(console.error);
  }, [saveWidget]);
  const isEdition = !!widget;
  const router = useSMLRouter();

  const goToCatalog = useCallback(() => {
    router.popToRoot();
    router.push(SMLRoutes.catalog);
  }, [router]);

  const goToChannels = useCallback(() => {
    router.popToRoot();
    router.push(SMLRoutes.promoteChannels);
  }, [router]);

  const backRouteParams = useMemo(() => ({ accountId, smartLinkId }), [accountId, smartLinkId]);
  const profileCount = widget?.data.socialLinks.length ?? 0;
  return (
    <SMLFormContainer
      backRoute={SMLRoutes.details}
      backRouteParams={backRouteParams}
      title={intl.formatMessage({
        id: 'app.smart_links.widgets.social_links.title',
        defaultMessage: 'Social links widget',
      })}
      help={intl.formatMessage({
        id: 'app.smart-links.widgets.social.connections_note',
        defaultMessage:
          "This block shows the artist's profiles from Catalog connections and Promote channels. Add or change a profile there.",
      })}
      onSubmit={isEdition ? undefined : onSave}
      onDelete={!isEdition ? undefined : deleteWidget}
      isLoading={isLoading}
      error={error}
    >
      {isEdition && profileCount === 0 && (
        <p className="text-muted">
          {intl.formatMessage({
            id: 'app.smart-links.widgets.social.empty',
            defaultMessage: 'No profiles yet. The block stays hidden on the page until you add one.',
          })}
        </p>
      )}
      <SMLButton onClick={goToCatalog}>
        {intl.formatMessage({
          id: 'app.smart-links.widgets.social.open_catalog',
          defaultMessage: 'Open Catalog connections',
        })}
      </SMLButton>
      <SMLButton onClick={goToChannels}>
        {intl.formatMessage({
          id: 'app.smart-links.widgets.social.open_channels',
          defaultMessage: 'Open Promote channels',
        })}
      </SMLButton>
    </SMLFormContainer>
  );
};

export const SocialWidgetEditionView = TypedMemo(SocialWidgetEditionViewComponent);
